import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { LOGO_URL } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";

export default function Login() {
    const { user, loading, login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    if (!loading && user) return <Navigate to="/" replace />;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setBusy(true);
        const r = await login(email.trim(), password);
        setBusy(false);
        if (!r.ok) setError(r.error || "Login failed");
        else navigate("/", { replace: true });
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center px-4">
            <div className="w-full max-w-sm" data-testid="login-card">
                <div className="flex flex-col items-center mb-8">
                    <img src={LOGO_URL} alt="A+ Student Loans" className="h-14 w-14 rounded-md object-contain" />
                    <h1 className="mt-4 text-xl font-display font-semibold tracking-tight text-gray-900">
                        A+ CaseFlow
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">Internal communications operations</p>
                </div>

                <form
                    onSubmit={handleSubmit}
                    className="bg-white border border-border rounded-xl shadow-sm p-6 space-y-4"
                    data-testid="login-form"
                >
                    <div className="space-y-1.5">
                        <Label htmlFor="email">Email</Label>
                        <Input
                            id="email"
                            type="email"
                            autoComplete="email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            required
                            data-testid="login-email-input"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="password">Password</Label>
                        <Input
                            id="password"
                            type="password"
                            autoComplete="current-password"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            required
                            data-testid="login-password-input"
                        />
                    </div>
                    {error && (
                        <div
                            className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2"
                            data-testid="login-error"
                        >
                            {error}
                        </div>
                    )}
                    <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit-button">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
                    </Button>
                </form>

                <p className="text-xs text-muted-foreground text-center mt-6">
                    Staff access only. Contact your admin if you need an account.
                </p>
            </div>
        </div>
    );
}
